import axios from 'axios';
import { config } from '../config/env';

if (!config.apiUrl) {
  throw new Error('API URL is not configured. Please set REACT_APP_API_URL environment variable.');
}

const API_BASE_URL = config.apiUrl;
const REPORTS_SERVER_URL = config.reportsServerUrl || config.apiUrl;

// Log reports server URL for debugging
console.log('Reports Server URL:', REPORTS_SERVER_URL);

// Helper function to get the JWT token from localStorage
const getToken = () => localStorage.getItem('token');

// Event name for organization ID changes
export const ORG_ID_CHANGE_EVENT = 'orgIdChange';

// Event for authentication status
export const AUTH_STATUS_EVENT = 'authStatusChange';

// Function to get current organization ID from localStorage
export const getCurrentOrgId = () => {
  return localStorage.getItem('orgId');
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Organization-ID': getCurrentOrgId()
  },
});

// Add a queue for requests that failed due to 401
let isRefreshing = false;
let failedQueue = [];
let refreshTokenPromise = null;

const processFailedQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Helper to clear all auth data
const clearAuthData = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('userRoles');
  localStorage.removeItem('displayName');
  localStorage.removeItem('orgId');
};

// Helper to store auth data
const storeAuthData = (data) => {
  const { token, refreshToken, uid, roles, displayName, orgId } = data;
  
  // Validate required tokens
  if (!token || !refreshToken) {
    throw new Error('Invalid authentication data: missing required tokens');
  }

  // Store tokens
  localStorage.setItem('token', token);
  localStorage.setItem('refreshToken', refreshToken);
  
  // Store user data
  localStorage.setItem('userId', uid);
  if (roles) localStorage.setItem('userRoles', JSON.stringify(roles));
  if (displayName) localStorage.setItem('displayName', displayName);
  if (orgId) localStorage.setItem('orgId', orgId);

  // Log success (without sensitive data)
  console.log('Authentication data stored successfully');
};

// Function to refresh token with proper synchronization
const refreshTokenInternal = async () => {
  const refreshTokenValue = localStorage.getItem('refreshToken');
  if (!refreshTokenValue) {
    throw new Error('No refresh token available');
  }

  try {
    console.log('Explicitly refreshing token...');
    const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
      refreshToken: refreshTokenValue
    });

    console.log('Refresh token response:', response.data);

    const { token, refreshToken: newRefreshToken, user } = response.data;
    
    if (!token || !newRefreshToken || !user) {
      console.error('Missing required data in response:', response.data);
      throw new Error('Invalid refresh token response');
    }

    // Store tokens
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', newRefreshToken);
    
    // Store user data
    localStorage.setItem('userId', user.uid);
    localStorage.setItem('userRoles', JSON.stringify(user.roles));
    localStorage.setItem('displayName', user.displayName);
    localStorage.setItem('email', user.email);
    if (user.orgId) localStorage.setItem('orgId', user.orgId);
    if (user.photoURL) localStorage.setItem('photoURL', user.photoURL);

    window.dispatchEvent(new CustomEvent(AUTH_STATUS_EVENT, {
      detail: { 
        status: 'refreshed',
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          roles: user.roles,
          orgId: user.orgId
        }
      }
    }));

    return response.data;
  } catch (error) {
    clearAuthData();

    window.dispatchEvent(new CustomEvent(AUTH_STATUS_EVENT, {
      detail: {
        status: 'expired',
        message: error.response?.data?.message || 'Session expired'
      }
    }));

    throw error;
  }
};

// Synchronized token refresh function
export const refreshTokenManually = async () => {
  console.log('[Token Refresh] refreshTokenManually called, isRefreshing:', isRefreshing);
  
  if (isRefreshing) {
    console.log('[Token Refresh] Already refreshing, waiting for existing promise');
    return refreshTokenPromise;
  }

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    console.error('[Token Refresh] No refresh token in localStorage');
    const error = new Error('No refresh token available - user must login again');
    error.code = 'NO_REFRESH_TOKEN';
    throw error;
  }

  try {
    isRefreshing = true;
    console.log('[Token Refresh] Starting token refresh, refreshToken present:', !!refreshToken);
    refreshTokenPromise = refreshTokenInternal();
    const refreshResult = await refreshTokenPromise;
    
    console.log('[Token Refresh] Refresh completed, result:', {
      hasResult: !!refreshResult,
      hasToken: !!(refreshResult && refreshResult.token),
      tokenLength: refreshResult?.token?.length,
      hasUser: !!refreshResult?.user
    });
    
    if (!refreshResult || !refreshResult.token) {
      console.error('[Token Refresh] Invalid refresh result:', refreshResult);
      clearAuthData();
      throw new Error('Session refresh failed: Invalid response');
    }

    // Update axios default headers
    api.defaults.headers.common['Authorization'] = `Bearer ${refreshResult.token}`;
    
    console.log('[Token Refresh] Token refresh successful, new token stored');
    
    // Dispatch success event
    window.dispatchEvent(new CustomEvent(AUTH_STATUS_EVENT, {
      detail: { 
        status: 'refreshed',
        user: {
          uid: refreshResult.user?.uid || refreshResult.uid,
          email: refreshResult.user?.email || refreshResult.email,
          displayName: refreshResult.user?.displayName || refreshResult.displayName,
          roles: refreshResult.user?.roles || refreshResult.roles,
          orgId: refreshResult.user?.orgId || refreshResult.orgId
        }
      }
    }));

    return refreshResult;
  } catch (error) {
    console.error('[Token Refresh] Token refresh failed:', {
      message: error.message,
      code: error.code,
      responseStatus: error.response?.status,
      responseData: error.response?.data
    });
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      clearAuthData();
      const authError = new Error('Refresh token expired - user must login again');
      authError.code = 'REFRESH_TOKEN_EXPIRED';
      throw authError;
    }
    
    // Clear auth data on any refresh failure
    clearAuthData();
    throw error;
  } finally {
    isRefreshing = false;
    refreshTokenPromise = null;
    console.log('[Token Refresh] Refresh process completed, isRefreshing reset to false');
  }
};

// Add response interceptor with improved error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors
    if (!error.response) {
      window.dispatchEvent(new CustomEvent(AUTH_STATUS_EVENT, {
        detail: {
          status: 'network_error',
          message: 'Network error. Please check your connection.'
        }
      }));
      return Promise.reject(error);
    }

    const status = error.response.status;

    // Handle 401 errors with improved retry logic
    if (status === 401 && !originalRequest._retry) {
      const isBulkUpdate = originalRequest.url && originalRequest.url.includes('bulk-update');
      
      // Log the 401 error for debugging
      if (isBulkUpdate) {
        console.log('[Response Interceptor] 401 error on bulk update:', {
          url: originalRequest.url,
          hadAuthHeader: !!originalRequest.headers?.Authorization,
          authHeaderPrefix: originalRequest.headers?.Authorization?.substring(0, 20),
          refreshTokenPresent: !!localStorage.getItem('refreshToken'),
          tokenPresent: !!localStorage.getItem('token')
        });
      }
      
      // Skip token refresh for bulk update requests that might be timing out
      // These will be retried with the same token if it's just a timeout
      if (isBulkUpdate) {
        // For bulk updates, check if it's a timeout vs actual auth error
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          // Timeout error, don't invalidate session - just retry once
          if (!originalRequest._timeoutRetry) {
            originalRequest._timeoutRetry = true;
            return api(originalRequest);
          }
        }
      }

      if (isRefreshing) {
        // Queue this request while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        console.log('[Response Interceptor] Attempting to refresh token for 401 error');
        const refreshResult = await refreshTokenManually();
        console.log('[Response Interceptor] Token refresh completed, result:', {
          hasResult: !!refreshResult,
          hasToken: !!(refreshResult && refreshResult.token),
          resultKeys: refreshResult ? Object.keys(refreshResult) : []
        });
        
        const newToken = refreshResult && refreshResult.token;
        
        if (!newToken) {
          console.error('[Response Interceptor] No token found in refreshResult:', {
            refreshResult,
            refreshResultType: typeof refreshResult,
            refreshResultKeys: refreshResult ? Object.keys(refreshResult) : [],
            refreshResultToken: refreshResult?.token,
            refreshResultUser: refreshResult?.user
          });
          if (isBulkUpdate) {
            console.error('[Response Interceptor] Bulk update failed: token refresh returned no token');
          }
          clearAuthData();
          // Optionally, force logout or redirect to login
          return Promise.reject(new Error('Session refresh failed: No token returned'));
        }
        
        // Update token in localStorage
        localStorage.setItem('token', newToken);
        
        // Update headers for future requests
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        
        // Process queued requests
        processFailedQueue(null, newToken);
        
        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        
        if (isBulkUpdate) {
          console.log('[Response Interceptor] Retrying bulk update with refreshed token');
        }
        
        return api(originalRequest);
      } catch (refreshError) {
        // Process queued requests with error
        processFailedQueue(refreshError, null);
        
        if (isBulkUpdate) {
          console.error('[Response Interceptor] Token refresh failed for bulk update:', refreshError);
        }
        
        // Only clear auth data if it's a real auth error, not a timeout
        if (refreshError.response?.status === 401 || refreshError.message?.includes('expired')) {
          clearAuthData();
          window.dispatchEvent(new CustomEvent(AUTH_STATUS_EVENT, {
            detail: {
              status: 'refresh_failed',
              message: refreshError.message || 'Session expired'
            }
          }));
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle 403 errors
    if (status === 403) {
      window.dispatchEvent(new CustomEvent(AUTH_STATUS_EVENT, {
        detail: {
          status: 'permission_denied',
          message: error.response?.data?.message || 'Permission denied'
        }
      }));
    }

    return Promise.reject(error);
  }
);

// Add request interceptor for token validation
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('orgId') || '1066';
    
    // Enhanced logging for bulk update requests
    const isBulkUpdate = config.url?.includes('bulk-update');
    if (isBulkUpdate) {
      console.log('[Request Interceptor] Bulk update request detected:', {
        url: config.url,
        tokenPresent: !!token,
        tokenLength: token?.length,
        orgId,
        method: config.method,
        existingHeaders: Object.keys(config.headers || {})
      });
    }

    // Always ensure headers object exists
    if (!config.headers) {
      config.headers = {};
    }

    if (token) {
      // Explicitly set the Authorization header (axios normalizes header names)
      // Set both capitalized and lowercase to ensure compatibility
      config.headers.Authorization = `Bearer ${token}`;
      config.headers.authorization = `Bearer ${token}`;
      if (isBulkUpdate) {
        console.log('[Request Interceptor] Authorization header set for bulk update:', {
          headerValue: `Bearer ${token.substring(0, 20)}...`,
          fullHeaderLength: config.headers.Authorization?.length,
          bothHeadersSet: !!(config.headers.Authorization && config.headers.authorization)
        });
      }
    } else {
      // Remove Authorization header if token is missing
      delete config.headers.Authorization;
      delete config.headers.authorization;
      if (isBulkUpdate) {
        console.warn('[Request Interceptor] WARNING: No token found for bulk update request!');
      }
    }
    
    // Always set organization ID - ensure it's set on the headers object
    config.headers['X-Organization-ID'] = orgId;
    
    // Enhanced logging for bulk update requests
    if (isBulkUpdate) {
      console.log('[Request Interceptor] Final headers for bulk update:', {
        hasAuthorization: !!config.headers.Authorization,
        authorizationPrefix: config.headers.Authorization?.substring(0, 30) + '...',
        authorizationFullLength: config.headers.Authorization?.length,
        organizationId: config.headers['X-Organization-ID'],
        contentType: config.headers['Content-Type'],
        allHeaderKeys: Object.keys(config.headers)
      });
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Helper function to get headers with auth token and org ID
const getHeaders = () => {
  const token = localStorage.getItem('token');
  const orgId = localStorage.getItem('orgId') || '1066';
  const headers = {
    'Content-Type': 'application/json',
    'X-Organization-ID': orgId
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// Cache for API responses
const apiCache = new Map();
const pendingApiRequests = new Map();

// Cache timeout in milliseconds (5 minutes)
const CACHE_TIMEOUT = 5 * 60 * 1000;

// Helper function to create a cache key
const createCacheKey = (endpoint, params = {}) => {
  // Include orgId in cache key if it's part of the endpoint or params
  const orgId = params.orgId || (endpoint.includes('/organization/') ? endpoint.split('/organization/')[1]?.split('/')[0] : null);
  return `${endpoint}:${orgId || ''}:${JSON.stringify(params)}`;
};

// Helper function to handle API requests with caching and deduplication
const handleRequest = async (endpoint, requestFn) => {
  const cacheKey = createCacheKey(endpoint);

  // Check cache first
  const cachedData = apiCache.get(cacheKey);
  if (cachedData && (Date.now() - cachedData.timestamp) < CACHE_TIMEOUT) {
    return cachedData.data;
  }

  // Check for pending requests
  if (pendingApiRequests.has(cacheKey)) {
    return pendingApiRequests.get(cacheKey);
  }

  // Create new request promise
  const requestPromise = requestFn().then(response => {
    // Cache the successful response
    apiCache.set(cacheKey, {
      data: response,
      timestamp: Date.now()
    });
    pendingApiRequests.delete(cacheKey);
    return response;
  }).catch(error => {
    pendingApiRequests.delete(cacheKey);
    throw error;
  });

  // Store the pending request
  pendingApiRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

// Clear cache for specific endpoint or all cache if no endpoint provided
export const clearCache = (endpoint = null) => {
  if (endpoint) {
    const cacheKey = createCacheKey(endpoint);
    apiCache.delete(cacheKey);
  } else {
    apiCache.clear();
  }
};

// Auth endpoints
export const login = async (credentials) => {
  try {
    const { email, password } = credentials;
    const response = await api.post('/auth/login', { email, password });

    // Store auth data in localStorage
    localStorage.setItem('token', response.token);
    localStorage.setItem('refreshToken', response.refreshToken); // Store refresh token
    localStorage.setItem('userId', response.uid);
    localStorage.setItem('userRoles', JSON.stringify(response.roles));
    if (response.displayName) {
      localStorage.setItem('displayName', response.displayName);
    }

    // Use default orgId if not provided
    const previousOrgId = localStorage.getItem('orgId');
    const newOrgId = response.orgId || '169';
    const timestamp = new Date().toISOString();

    // Track organization change in history if different
    if (previousOrgId !== newOrgId) {
      try {
        // Get existing history or initialize empty array
        const orgIdHistory = JSON.parse(localStorage.getItem('orgIdHistory') || '[]');

        // Add new entry to history (limit to last 10 entries)
        orgIdHistory.unshift({
          timestamp,
          previousOrgId,
          newOrgId,
          source: 'login'
        });

        // Keep only the last 10 entries
        if (orgIdHistory.length > 10) {
          orgIdHistory.length = 10;
        }

        // Save back to localStorage
        localStorage.setItem('orgIdHistory', JSON.stringify(orgIdHistory));
      } catch (e) {
        console.error('Error tracking orgId change during login:', e);
      }
    }

    localStorage.setItem('orgId', newOrgId);

    // Dispatch the org ID change event with full details
    window.dispatchEvent(new CustomEvent(ORG_ID_CHANGE_EVENT, {
      detail: {
        orgId: newOrgId,
        timestamp,
        previousOrgId,
        source: 'login'
      }
    }));

    return response;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const loginBasic = async (email, password) => {
  const basicAuth = btoa(`${email}:${password}`);
  const response = await api.post('/auth/login/basic', null, {
    headers: {
      Authorization: `Basic ${basicAuth}`
    }
  });
  const { token, refreshToken, uid, roles, displayName, photoURL } = response.data;

  // Store auth data in localStorage
  localStorage.setItem('token', token);
  localStorage.setItem('refreshToken', refreshToken); // Store refresh token
  localStorage.setItem('userId', uid);
  localStorage.setItem('userRoles', JSON.stringify(roles));
  localStorage.setItem('displayName', displayName);

  // Use default orgId if not provided
  const newOrgId = '169';
  localStorage.setItem('orgId', newOrgId);
  // Dispatch the org ID change event
  window.dispatchEvent(new CustomEvent(ORG_ID_CHANGE_EVENT, { detail: newOrgId }));

  return response.data;
};

export const logout = async () => {
  const response = await api.post('/auth/logout');
  // Clear auth-related items from localStorage
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken'); // Remove refresh token
  localStorage.removeItem('userId');
  localStorage.removeItem('userRoles');
  localStorage.removeItem('displayName');
  localStorage.removeItem('orgId');
  return response.data;
};

export const verifyAuth = async () => {
  const response = await api.get('/auth/verify');
  return response.data;
};

// New function to explicitly refresh the token
export const refreshToken = async () => {
  const refreshTokenValue = localStorage.getItem('refreshToken');
  if (!refreshTokenValue) {
    console.error('No refresh token available in localStorage');
    throw new Error('No refresh token available');
  }

  try {
    console.log('Explicitly refreshing token...');
    const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
      refreshToken: refreshTokenValue
    });

    console.log('Refresh token response:', response.data);

    const { token, refreshToken: newRefreshToken, uid, roles, displayName } = response.data;

    if (!token || !newRefreshToken) {
      console.error('Missing token or refreshToken in response:', response.data);
      throw new Error('Invalid refresh token response');
    }

    // Store the new tokens
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', newRefreshToken);

    // Update other user info if available
    if (uid) localStorage.setItem('userId', uid);
    if (roles) localStorage.setItem('userRoles', JSON.stringify(roles));
    if (displayName) localStorage.setItem('displayName', displayName);

    console.log('Token refreshed successfully');

    return response.data;
  } catch (error) {
    console.error('Error refreshing token:', error);
    console.error('Error response:', error.response?.data || 'No response data');
    console.error('Error status:', error.response?.status || 'No status code');



    // Clear auth data after failed refresh
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('userRoles');
    localStorage.removeItem('displayName');

    // Dispatch event that authentication expired/failed
    window.dispatchEvent(new CustomEvent(AUTH_STATUS_EVENT, {
      detail: { status: 'expired' }
    }));

    throw error;
  }
};

export const updateOrganization = async (orgId) => {
  const previousOrgId = localStorage.getItem('orgId');
  const timestamp = new Date().toISOString();
  const newOrgId = orgId || '1066';

  // Track the change in localStorage history
  try {
    // Get existing history or initialize empty array
    const orgIdHistory = JSON.parse(localStorage.getItem('orgIdHistory') || '[]');

    // Add new entry to history (limit to last 10 entries)
    orgIdHistory.unshift({
      timestamp,
      previousOrgId,
      newOrgId,
      source: 'api_update'
    });

    // Keep only the last 10 entries
    if (orgIdHistory.length > 10) {
      orgIdHistory.length = 10;
    }

    // Save back to localStorage
    localStorage.setItem('orgIdHistory', JSON.stringify(orgIdHistory));
  } catch (e) {
    console.error('Error tracking orgId change in API:', e);
  }

  // Make the API call
  const response = await api.put('/auth/organization', { orgId: newOrgId });

  // Store in localStorage
  localStorage.setItem('orgId', newOrgId);

  // Ensure the API interceptors use the updated orgId for all future requests
  api.defaults.headers.common['x-organization-id'] = newOrgId;

  // Clear cached data as it may be org-specific
  clearCache();

  // Dispatch the change event with detailed info
  window.dispatchEvent(new CustomEvent(ORG_ID_CHANGE_EVENT, {
    detail: {
      orgId: newOrgId,
      timestamp,
      previousOrgId,
      source: 'api_update'
    }
  }));

  // Log the change
  console.log(`API: Organization ID changed from ${previousOrgId} to ${newOrgId} at ${timestamp}`);

  // Return the response data
  return response.data;
};

// MSSQL endpoints
export const getMssqlStatus = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.get('/mssql/status');
    return response.data;
  } catch (error) {
    console.error('Error getting MSSQL status:', error);
    throw error;
  }
};

export const getCirclesByOrgId = async (orgId) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/mssql/organization/${orgId}/circles`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/circles`);
      return response.data;
    } catch (error) {
      console.error('Error getting circles:', error);
      throw error;
    }
  });
};

export const getGradingSystemsByOrgId = async (orgId) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/mssql/organization/${orgId}/grading-systems`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/grading-systems`);
      return response.data;
    } catch (error) {
      console.error('Error getting grading systems:', error);
      throw error;
    }
  });
};

export const getGradeHierarchy = async (orgId) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/mssql/organization/${orgId}/grade-hierarchy`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/grade-hierarchy`);
      return response.data;
    } catch (error) {
      console.error('Error getting grade hierarchy:', error);
      throw error;
    }
  });
};

export const getOrganizationActiveGrades = async (orgId) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/mssql/organization/${orgId}/active-grades`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/active-grades`);
      return response.data;
    } catch (error) {
      console.error('Error getting active grades:', error);
      throw error;
    }
  });
}

/**
 * Get active grades using LEGACY logic (employee-based) - only grades with current employees.
 * For KAM comparison/testing purposes.
 */
export async function getActiveGradesLegacy(orgId) {
  if (!orgId) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/mssql/organization/${orgId}/active-grades-legacy`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/active-grades-legacy`);
      return response.data;
    } catch (error) {
      console.error('Error getting legacy active grades:', error);
      throw error;
    }
  });
};

// ============================================================
// Organization Settings API
// ============================================================

/**
 * Get organization settings (grade source preference, fallback settings)
 */
export async function getOrganizationSettings(orgId) {
  if (!orgId) {
    throw new Error('Organization ID required');
  }
  return handleRequest(`/organization-settings/${orgId}`, async () => {
    try {
      const response = await api.get(`/organization-settings/${orgId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting organization settings:', error);
      throw error;
    }
  });
}

/**
 * Update organization settings
 * @param {string} orgId - Organization ID
 * @param {Object} settings - Settings to update
 * @param {string} settings.gradeSourcePreference - 'permission_based', 'employee_based', or 'auto'
 * @param {boolean} settings.allowLegacyFallback - Whether to allow fallback to employee-based grades
 */
export async function updateOrganizationSettings(orgId, settings) {
  if (!orgId) {
    throw new Error('Organization ID required');
  }
  try {
    const response = await api.put(`/organization-settings/${orgId}`, settings);
    return response.data;
  } catch (error) {
    console.error('Error updating organization settings:', error);
    throw error;
  }
}

/**
 * Delete organization settings (revert to defaults)
 */
export async function deleteOrganizationSettings(orgId) {
  if (!orgId) {
    throw new Error('Organization ID required');
  }
  try {
    const response = await api.delete(`/organization-settings/${orgId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting organization settings:', error);
    throw error;
  }
}

export const getActiveGradesTranslation = async (orgId) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/mssql/organization/${orgId}/active-grades-translation`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/active-grades-translation`);
      return response.data;
    } catch (error) {
      console.error('Error getting active grades translation:', error);
      throw error;
    }
  });
};

export const hasBaseGrades = async (orgId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.get(`/mssql/organization/${orgId}/has-base-grades`);
    return response.data;
  } catch (error) {
    console.error('Error checking if organization has base grades:', error);
    throw error;
  }
};


export const getCirclePercentileData = async (circleIds) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.post('/mssql/circle/percentile', { circleIds });
    return response.data;
  } catch (error) {
    console.error('Error getting circle percentile data:', error);
    throw error;
  }
};

export const getGradePercentileData = async (circleIds) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.post('/mssql/grade/percentile', { circleIds });
    return response.data;
  } catch (error) {
    console.error('Error getting grade percentile data:', error);
    throw error;
  }
};


export const verifyUser = async (userId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.post('/mssql/verify-user', { userId });
    return response.data;
  } catch (error) {
    console.error('Error verifying user:', error);
    throw error;
  }
};

// Get uploaded employees for an organization
export const getUploadedEmployees = async (orgId) => {
  return handleRequest(`/mssql/organization/${orgId}/uploaded-employees`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/uploaded-employees`);
      return response.data;
    } catch (error) {
      console.error('Error fetching uploaded employees:', error);
      throw error;
    }
  });
};

// Get employee statistics including variance and delta
export const getEmployeeStatistics = async (orgId) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/mssql/organization/${orgId}/employee-stats`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/employee-stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching employee statistics:', error);
      throw error;
    }
  });
};

// Get active grades mapped to Paterson Classic and Modern
export const getActiveGradesPatersonMapping = async (orgId) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/mssql/organization/${orgId}/active-grades-paterson`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/active-grades-paterson`);
      return response.data;
    } catch (error) {
      console.error('Error fetching active grades Paterson mapping:', error);
      throw error;
    }
  });
};

// Get granted grades for an organization
export const getGrantedGrades = async (orgId) => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/mssql/organization/${orgId}/granted-grades`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/granted-grades`);
      return response.data;
    } catch (error) {
      console.error('Error fetching granted grades:', error);
      throw error;
    }
  });
};

// Get staging employees for an organization
export const getStagingEmployees = async (orgId) => {
  return handleRequest(`/mssql/organization/${orgId}/staging-employees`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/staging-employees`);
      return response.data;
    } catch (error) {
      console.error('Error fetching staging employees:', error);
      throw error;
    }
  });
};

// Get snapshot employee data for an organization
export const getSnapshotEmployeeData = async (orgId) => {
  return handleRequest(`/mssql/organization/${orgId}/snapshot-employee-data`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/snapshot-employee-data`);
      return response.data;
    } catch (error) {
      console.error('Error fetching snapshot employee data:', error);
      throw error;
    }
  });
};

// Get imported employees for an organization
export const getImportedEmployees = async (orgId) => {
  return handleRequest(`/mssql/organization/${orgId}/imported-employees`, async () => {
    try {
      const response = await api.get(`/mssql/organization/${orgId}/imported-employees`);
      return response.data;
    } catch (error) {
      console.error('Error fetching imported employees:', error);
      throw error;
    }
  });
};

// Get uploaded employees with LTI and STI amounts from PostgreSQL
export const getUploadedEmployeesWithAmounts = async (orgId) => {
  try {
    const response = await api.get(`/data-augmentation/organization/${orgId}/uploaded-employees`);
    return response.data;
  } catch (error) {
    console.error('Error fetching uploaded employees with amounts:', error);
    throw error;
  }
};

// Update LTI and STI amounts for a single employee
export const updateEmployeeAmounts = async (orgId, uploadedEmployeeId, ltiAmount, ontargetLtiAmount, employeeNumber) => {
  try {
    // Ensure values are properly formatted - handle null, undefined, empty string, and numbers
    let ltiAmountValue = null;
    if (ltiAmount !== null && ltiAmount !== undefined && ltiAmount !== '') {
      const parsed = typeof ltiAmount === 'string' ? parseFloat(ltiAmount) : ltiAmount;
      ltiAmountValue = isNaN(parsed) ? null : parsed;
    }

    let ontargetLtiAmountValue = null;
    if (ontargetLtiAmount !== null && ontargetLtiAmount !== undefined && ontargetLtiAmount !== '') {
      const parsed = typeof ontargetLtiAmount === 'string' ? parseFloat(ontargetLtiAmount) : ontargetLtiAmount;
      ontargetLtiAmountValue = isNaN(parsed) ? null : parsed;
    }

    const response = await api.put(
      `/data-augmentation/organization/${orgId}/uploaded-employees/${uploadedEmployeeId}`,
      {
        ltiAmount: ltiAmountValue,
        ontargetLtiAmount: ontargetLtiAmountValue,
        employeeNumber: employeeNumber || '',
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating employee amounts:', error);
    throw error;
  }
};

// Helper function to retry bulk update with exponential backoff
const bulkUpdateWithRetry = async (orgId, updates, maxRetries, baseDelay) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await api.post(
        `/data-augmentation/organization/${orgId}/uploaded-employees/bulk-update`,
        { updates },
        {
          timeout: 300000, // 5 minutes timeout for bulk updates
        }
      );
      return response.data;
    } catch (error) {
      lastError = error;
      
      // Check if it's a connection error that we should retry
      const isConnectionError = 
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.code === 'ETIMEDOUT' ||
        error.code === 'ERR_NETWORK' ||
        error.message?.includes('timeout') ||
        error.message?.includes('ECONNREFUSED') ||
        (error.response?.status >= 500 && error.response?.status < 600);
      
      // Don't retry on client errors (4xx) except 408 (timeout)
      if (!isConnectionError && error.response?.status !== 408) {
        throw error;
      }
      
      // If we've exhausted retries, throw
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Calculate exponential backoff delay
      const delay = baseDelay * Math.pow(2, attempt);
      console.log(`[BulkUpdate] Connection error, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})...`, {
        code: error.code,
        message: error.message,
        status: error.response?.status
      });
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

// Helper function to process large updates in chunks
const bulkUpdateInChunks = async (orgId, updates, chunkSize, maxRetries, retryDelay, onProgress) => {
  const chunks = [];
  for (let i = 0; i < updates.length; i += chunkSize) {
    chunks.push(updates.slice(i, i + chunkSize));
  }
  
  console.log(`[BulkUpdate] Processing ${updates.length} updates in ${chunks.length} chunks of ${chunkSize}`);
  
  let totalUpdated = 0;
  let totalCreated = 0;
  const allErrors = [];
  
  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    
    try {
      const result = await bulkUpdateWithRetry(orgId, chunk, maxRetries, retryDelay);
      
      totalUpdated += result.updated || 0;
      totalCreated += result.created || 0;
      if (result.errors) {
        allErrors.push(...result.errors);
      }
      
      // Report progress
      if (onProgress) {
        onProgress({
          processed: Math.min((i + 1) * chunkSize, updates.length),
          total: updates.length,
          chunk: i + 1,
          totalChunks: chunks.length,
          updated: totalUpdated,
          created: totalCreated,
          errors: allErrors.length
        });
      }
      
      console.log(`[BulkUpdate] Chunk ${i + 1}/${chunks.length} completed: ${result.updated} updated, ${result.created} created`);
      
      // Small delay between chunks to avoid overwhelming the server
      if (i < chunks.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    } catch (error) {
      console.error(`[BulkUpdate] Chunk ${i + 1}/${chunks.length} failed:`, error);
      
      // Add chunk errors to allErrors
      chunk.forEach((update) => {
        allErrors.push({
          id: update.uploadedEmployeeId || update.snapshotEmployeeDataId || 0,
          error: `Chunk processing failed: ${error.message}`
        });
      });
      
      // Continue with next chunk instead of failing completely
      // This allows partial success
    }
  }
  
  return {
    updated: totalUpdated,
    created: totalCreated,
    errors: allErrors
  };
};

// Bulk update LTI and STI amounts for multiple employees
export const bulkUpdateEmployeeAmounts = async (orgId, updates, options = {}) => {
  const { 
    maxRetries = 3, 
    retryDelay = 2000,
    chunkSize = 500, // Process in chunks of 500
    onProgress 
  } = options;
  
  try {
    // Verify token exists before making request (same as individual update pattern)
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('[BulkUpdate] No token in localStorage before request');
      throw new Error('No authentication token found. Please log in again.');
    }
    
    console.log('[BulkUpdate] Making request with token present:', {
      tokenLength: token.length,
      orgId,
      updateCount: updates.length,
      chunkSize,
      willChunk: updates.length > chunkSize
    });
    
    // For large updates, process in chunks
    if (updates.length > chunkSize) {
      return await bulkUpdateInChunks(orgId, updates, chunkSize, maxRetries, retryDelay, onProgress);
    }
    
    // For smaller updates, use single request with retry
    return await bulkUpdateWithRetry(orgId, updates, maxRetries, retryDelay);
    
  } catch (error) {
    console.error('[BulkUpdate] Error bulk updating employee amounts:', {
      message: error.message,
      status: error.response?.status,
      code: error.code,
      hasToken: !!localStorage.getItem('token')
    });
    throw error;
  }
};

// Trigger sync from MSSQL to PostgreSQL for an organization
export const triggerDataSync = async (orgId) => {
  try {
    const response = await api.post(
      `/data-augmentation/organization/${orgId}/sync`,
      {},
      {
        timeout: 300000, // 5 minutes timeout for sync
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error triggering data sync:', error);
    throw error;
  }
};

export const getUserRoles = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.post('/mssql/user-roles');
    return response.data;
  } catch (error) {
    console.error('Error getting user roles:', error);
    throw error;
  }
};


// Questionnaire endpoints
export const createQuestionnaire = async (data) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.post('/questionnaires', {
      ...data,
      organizationId: getCurrentOrgId(),
      createdBy: getCurrentUserId(),
      updatedBy: getCurrentUserId()
    });
    return response.data;
  } catch (error) {
    console.error('Error creating questionnaire:', error);
    throw error;
  }
};

export const createQuestionnaireRaw = async (data) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.post('/questionnaires/raw', {
      ...data,
      organizationId: parseInt(getCurrentOrgId()),
      createdBy: getCurrentUserId(),
      updatedBy: getCurrentUserId()
    });
    return response.data;
  } catch (error) {
    console.error('Error creating questionnaire from raw data:', error);
    throw error;
  }
};

export const getQuestionnaires = () => {
  return handleRequest('questionnaires', () =>
    api.get('/questionnaires')
  );
};

export const getQuestionnairesByOrganization = async () => {
  const orgId = getCurrentOrgId();
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }
  return handleRequest(`/questionnaires/organization?organizationId=${orgId}`, async () => {
    try {
      const response = await api.get(`/questionnaires/organization?organizationId=${orgId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching questionnaires:', error);
      throw error;
    }
  });
};

export const getQuestionnaireById = async (id) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.get(`/questionnaires/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching questionnaire:', error);
    throw error;
  }
};

export const updateQuestionnaire = async (id, data) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.patch(`/questionnaires/${id}`, {
      ...data,
      updatedBy: getCurrentUserId()
    });
    return response.data;
  } catch (error) {
    console.error('Error updating questionnaire:', error);
    throw error;
  }
};

export const updateQuestionnaireRaw = async (id, data) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.patch(`/questionnaires/${id}/raw`, {
      ...data,
      organizationId: parseInt(getCurrentOrgId()),
      updatedBy: getCurrentUserId(),
    });
    return response.data;
  } catch (error) {
    console.error('Error updating questionnaire from raw data:', error);
    throw error;
  }
};

export const getQuestionnaireAnswers = async (id) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.get(`/questionnaires/${id}/answers`);
    return response.data;
  } catch (error) {
    console.error('Error fetching questionnaire answers:', error);
    throw error;
  }
};

export const deleteQuestionnaire = async (id) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    await api.delete(`/questionnaires/${id}`);
  } catch (error) {
    console.error('Error deleting questionnaire:', error);
    throw error;
  }
};

// Report APIs - Updated to match new API spec
export const generateReport = async (reportData) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.post('/reports/generate', reportData);
    return response.data;
  } catch (error) {
    console.error('Error generating report:', error);
    throw error;
  }
};

export const getReportsByOrganization = async (orgId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.get(`/reports/organization/${orgId}`);
    return response.data?.reports || [];
  } catch (error) {
    console.error('Error fetching reports:', error);
    throw error;
  }
};

export const getReportsUsageSummary = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.get('/reports/usage-summary');
    return response.data || [];
  } catch (error) {
    console.error('Error fetching reports usage summary:', error);
    throw error;
  }
};

export const getOrganizationNames = async (orgIds) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const ids = Array.isArray(orgIds) ? orgIds : [];
    if (ids.length === 0) return {};
    const response = await api.post('/mssql/organization-names', { orgIds: ids });
    return response.data || {};
  } catch (error) {
    console.error('Error fetching organization names:', error);
    throw error;
  }
};

export const getReportById = async (reportId) => {
  try { 
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.get(`/reports/${reportId}`);
    return response?.data || {};
  } catch (error) {
    console.error('Error fetching report:', error);
    throw error;
  }
};

export const getReportStatus = async (reportId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.get(`/reports/${reportId}/status`);
    return {
      status: response.data?.jobStatus?.status || 'UNKNOWN',
      progress: response.data?.jobStatus?.progress || 0,
      message: response.data?.jobStatus?.message || ''
    };
  } catch (error) {
    console.error('Error fetching report status:', error);
    throw error;
  }
};

export const deleteReport = async (reportId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.delete(`/reports/${reportId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting report:', error);
    throw error;
  }
};

// Create separate axios instance for reports server
const reportsApi = axios.create({
  baseURL: REPORTS_SERVER_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Organization-ID': getCurrentOrgId()
  },
});

// Add auth interceptor for reports API
reportsApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('orgId') || '1066';
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Reports API - Token added to request:', token.substring(0, 20) + '...');
    } else {
      console.warn('Reports API - No token found in localStorage');
      delete config.headers.Authorization;
    }
    config.headers['X-Organization-ID'] = orgId;
    console.log('Reports API - Request headers:', {
      hasAuthorization: !!config.headers.Authorization,
      'X-Organization-ID': config.headers['X-Organization-ID'],
      url: config.url
    });
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Export report to PDF
export const exportReportToPDF = async (reportId, reportData) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const url = `/report-server/reports/${reportId}/export/pdf`;
    console.log('Exporting PDF to:', REPORTS_SERVER_URL + url);
    console.log('Report ID:', reportId);
    const response = await reportsApi.post(
      url,
      reportData,
      {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    // Verify response is a PDF blob
    if (!(response.data instanceof Blob)) {
      throw new Error('Invalid response: expected PDF blob');
    }
    
    // Check content type
    const contentType = response.headers['content-type'] || response.data.type;
    if (contentType && !contentType.includes('application/pdf')) {
      console.warn(`Unexpected content type: ${contentType}, expected application/pdf`);
    }
    
    // Create blob URL with explicit PDF MIME type
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    
    // Return blob URL for modal preview
    // The caller will handle displaying it in a modal
    return blobUrl;
  } catch (error) {
    console.error('Error exporting report to PDF:', error);
    const errorMessage = error.response?.data?.error || error.message || 'Failed to export report to PDF';
    console.error('Error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: errorMessage
    });
    throw new Error(errorMessage);
  }
};

// Export report to Excel
export const exportReportToExcel = async (reportId, reportData, isKAMUser = false, circleName = null, gradingSystemName = null) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const url = `/report-server/reports/${reportId}/export/excel`;
    console.log('Exporting Excel to:', REPORTS_SERVER_URL + url);
    console.log('Report ID:', reportId);
    const response = await reportsApi.post(
      url,
      { ...reportData, isKAMUser, circleName, gradingSystemName },
      {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    // Create blob URL and trigger download
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', `report-${reportId}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    
    return { success: true };
  } catch (error) {
    console.error('Error exporting report to Excel:', error);
    const errorMessage = error.response?.data?.error || error.message || 'Failed to export report to Excel';
    console.error('Error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: errorMessage
    });
    throw new Error(errorMessage);
  }
};

// Export processed data to Excel
export const exportProcessedDataToExcel = async (reportId, metadata, circleId, gradingSystemId, organizationId, isKAMUser = false, circleName = null, gradingSystemName = null) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const url = `/report-server/reports/${reportId}/export/processed-data/excel`;
    console.log('Exporting processed data Excel to:', REPORTS_SERVER_URL + url);
    console.log('Report ID:', reportId);
    
    const response = await reportsApi.post(
      url,
      {
        metadata,
        circleId,
        gradingSystemId,
        organizationId,
        isKAMUser,
        circleName,
        gradingSystemName
      },
      {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    // Create blob URL and trigger download
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    const timestamp = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `processed-data-${reportId}-${timestamp}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    
    return { success: true };
  } catch (error) {
    console.error('Error exporting processed data to Excel:', error);
    const errorMessage = error.response?.data?.error || error.message || 'Failed to export processed data to Excel';
    console.error('Error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: errorMessage
    });
    throw new Error(errorMessage);
  }
};

// Export percentile source data to CSV (all grades)
export const exportPercentileSourceDataToExcel = async (reportId, circleId, circleName, gradingSystemId, organizationId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    
    // Call backend CSV endpoint directly (no reports server needed)
    const url = `/data-augmentation/percentile-source-data/csv?circleId=${circleId || ''}&requestingOrganizationId=${organizationId}`;
    console.log('Exporting percentile source data CSV to:', API_BASE_URL + url);
    console.log('Report ID:', reportId);
    
    const response = await api.get(url, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    // Create blob URL and trigger download
    const blob = new Blob([response.data], { 
      type: 'text/csv;charset=utf-8;' 
    });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    const timestamp = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `percentile-source-data-${reportId}-${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    
    return { success: true };
  } catch (error) {
    console.error('Error exporting percentile source data to CSV:', error);
    const errorMessage = error.response?.data?.error || error.message || 'Failed to export percentile source data to CSV';
    console.error('Error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: errorMessage
    });
    throw new Error(errorMessage);
  }
};

/**
 * Download percentile source data CSV for a single grade (KAM per-grade export).
 * @param {string} reportId - Report ID (for filename)
 * @param {string} circleId - Circle ID
 * @param {string} organizationId - Requesting organization ID
 * @param {string} gradeName - Grade name to filter (e.g. "A", "B Lower")
 * @param {string} [metric] - Metric field key (tgp, ltiAmount, ontargetLtiAmount, sti, onTargetSti). When set, CSV contains only rows that contributed to that metric's sample so count matches Market Sample.
 */
export const downloadPercentileSourceDataCsvForGrade = async (reportId, circleId, organizationId, gradeName, metric) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const encodedGrade = encodeURIComponent(gradeName || '');
    const metricParam = metric ? `&metric=${encodeURIComponent(metric)}` : '';
    const url = `/data-augmentation/percentile-source-data/csv?circleId=${circleId || ''}&requestingOrganizationId=${organizationId}&gradeName=${encodedGrade}${metricParam}`;
    const response = await api.get(url, {
      responseType: 'blob',
      headers: { 'Content-Type': 'application/json' },
    });
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const safeGrade = (gradeName || 'grade').replace(/[/\\?*:"]/g, '-');
    link.setAttribute('download', `percentile-source-data-${reportId}-${safeGrade}-${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    return { success: true };
  } catch (error) {
    console.error('Error downloading percentile source data CSV for grade:', error);
    const errorMessage = error.response?.data?.error || error.message || 'Failed to download CSV for grade';
    throw new Error(errorMessage);
  }
};

// Retranslate report grades
export const retranslateReportGrades = async (reportId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.post(`/reports/${reportId}/retranslate`);
    return response?.data || {};
  } catch (error) {
    console.error('Error retranslating report grades:', error);
    const errorMessage = error.response?.data?.error || error.message || 'Failed to retranslate report grades';
    throw new Error(errorMessage);
  }
};

// Recalculate percentiles for an existing report
export const recalculatePercentiles = async (reportId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }
    const response = await api.post(`/reports/${reportId}/recalculate-percentiles`);
    return response?.data || {};
  } catch (error) {
    console.error('Error recalculating percentiles:', error);
    const errorMessage = error.response?.data?.error || error.message || 'Failed to recalculate percentiles';
    throw new Error(errorMessage);
  }
};

// Helper functions for analysis - Using the new report APIs
export const analyzeCircleWithGradingSystem = async (circleId, gradingSystemId) => {
  try {
    const orgId = getCurrentOrgId();
    console.log(`Creating report for circleId: ${circleId}, gradingSystemId: ${gradingSystemId}`);

    const reportData = {
      name: `Analysis Report - ${new Date().toLocaleString()}`,
      description: `Analysis for Circle ${circleId} using Grading System ${gradingSystemId}`,
      circleId: circleId,
      gradingSystemId: gradingSystemId,
      organizationId: orgId
    };

    const response = await generateReport(reportData);
    console.log('Report generation response:', response);

    if (response?.success && response?.reportId) {
      return {
        jobId: response.reportId, // Map reportId to jobId for backward compatibility
        status: 'PENDING',
        createdAt: new Date().toISOString()
      };
    }

    throw new Error('Invalid response format - no report ID returned');
  } catch (error) {
    console.error(`Error creating report with circleId: ${circleId}, gradingSystemId: ${gradingSystemId}:`, error);
    throw error;
  }
};

export const getJobStatus = async (jobId) => {
  try {
    const response = await getReportStatus(jobId);
    console.log('Report status response:', response);

    return {
      status: response.status,
      progress: response.progress || 0,
      message: response.message || ''
    };
  } catch (error) {
    console.error(`Error fetching status for report ${jobId}:`, error);
    throw error;
  }
};

export const getJobResult = async (jobId) => {
  try {
    const response = await getReportById(jobId);
    console.log('Report result response:', response);

    return response;
  } catch (error) {
    console.error(`Error fetching result for report ${jobId}:`, error);
    throw error;
  }
};

// Helper functions for authentication
// const getAuthToken = () => {
//   return localStorage.getItem('token');
// };

const getCurrentUserId = () => {
  return localStorage.getItem('userId');
};

// Get circles for an organization
export const getCircles = async (orgId) => {
  try {
    return await getCirclesByOrgId(orgId);
  } catch (error) {
    console.error('Error fetching circles:', error);
    throw error;
  }
};

// Get grading systems for an organization
export const getGradingSystems = async (orgId) => {
  try {
    return await getGradingSystemsByOrgId(orgId);
  } catch (error) {
    console.error('Error fetching grading systems:', error);
    throw error;
  }
};

// Create a new report
export const createReport = async (reportData) => {
  try {
    // Extract circleId and gradingSystemId from the report data
    const { circleIds, gradingSystemId } = reportData;

    // For now, we'll use the first circle ID if multiple are provided
    const circleId = Array.isArray(circleIds) && circleIds.length > 0
      ? circleIds[0]
      : reportData.circleId;

    if (!circleId || !gradingSystemId) {
      throw new Error('Circle ID and Grading System ID are required');
    }

    // Use the correct endpoint by delegating to analyzeCircleWithGradingSystem
    return await analyzeCircleWithGradingSystem(circleId, gradingSystemId);
  } catch (error) {
    console.error('Error creating report:', error);
    throw error;
  }
};

export default api;